import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Check, ChevronsUpDown, X, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { projectService } from '@/services/project.service';
import { CreateProjectRequest, UpdateProjectRequest } from '@/types/models';
import { toast } from 'sonner';
import { getEmployeesForDropdown } from '@/services/employee.service';
import { getStatusesByCategory } from '@/services/status.service';

interface CreateProjectFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit?: (data: ProjectFormData) => void;
  onProjectCreated?: (project: any) => void;
  mode?: 'create' | 'edit';
  initialData?: Partial<ProjectFormData> | null;
  selectedProject?: any;
}

export interface ProjectFormData {
  projectName: string;
  client: string;
  description: string;
  status: string;
  priority: string;
  startDate: string;
  endDate: string;
  projectManager: string;
  teamMembers: string[];
  budget?: number;
}

// Fallback status options (for backward compatibility)
const FALLBACK_STATUS_OPTIONS = [
  { value: 'PLANNING', label: 'Planning' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'ON_HOLD', label: 'On Hold' },
];

const PRIORITY_OPTIONS = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
];

export function CreateProjectForm({ open, onOpenChange, onSubmit, onProjectCreated, mode = 'create', initialData, selectedProject }: CreateProjectFormProps) {
  const [formData, setFormData] = useState<ProjectFormData>({
    projectName: '',
    client: '',
    description: '',
    status: '',
    priority: '',
    startDate: '',
    endDate: '',
    projectManager: '',
    teamMembers: [],
    budget: 0,
  });

  const [errors, setErrors] = useState<Partial<ProjectFormData>>({});
  const [projectManagerOpen, setProjectManagerOpen] = useState(false);
  const [teamMembersOpen, setTeamMembersOpen] = useState(false);
  const [projectManagerSearch, setProjectManagerSearch] = useState('');
  const [teamMembersSearch, setTeamMembersSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [employees, setEmployees] = useState<Array<{id: string; name: string; employeeId?: string; email?: string}>>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [statusOptions, setStatusOptions] = useState<Array<{ value: string; label: string; id?: string }>>(FALLBACK_STATUS_OPTIONS);
  const [statusIdMap, setStatusIdMap] = useState<Map<string, string>>(new Map()); // Map status name to ID
  // Using Sonner toast directly

  // Fetch project statuses from API
  useEffect(() => {
    const fetchProjectStatuses = async () => {
      try {
        const projectStatuses = await getStatusesByCategory('project');
        const options = projectStatuses
          .filter(status => status.isActive)
          .map(status => ({
            value: status.name,
            label: status.name,
            id: status.id,
          }));
        
        // Create a map of status name to ID for easy lookup
        const idMap = new Map<string, string>();
        projectStatuses.forEach(status => {
          idMap.set(status.name, status.id);
        });
        setStatusIdMap(idMap);
        
        if (options.length > 0) {
          setStatusOptions(options);
        }
      } catch (error) {
        console.error('Error fetching project statuses:', error);
        // Keep fallback options on error
      }
    };

    if (open) {
      fetchProjectStatuses();
    }
  }, [open]);

  // Fetch employees from API
  const fetchEmployees = useCallback(async () => {
    try {
      setLoadingEmployees(true);
      const employeeData = await getEmployeesForDropdown();
      setEmployees(employeeData);
    } catch (error: any) {
      console.error('Error fetching employees:', error);
      toast.error("Error", {
        description: "Failed to load employee data. Please try again.",
      });
    } finally {
      setLoadingEmployees(false);
    }
  }, []);

  // Fetch employees when dialog opens
  useEffect(() => {
    if (open && employees.length === 0) {
      fetchEmployees();
    }
  }, [open, employees.length, fetchEmployees]);

  // Helper function to normalize status for matching (handles underscores, spaces, case)
  const normalizeStatusForMatching = (status: string): string => {
    if (!status) return '';
    return status
      .toLowerCase()
      .replace(/_/g, ' ')  // Replace underscores with spaces
      .replace(/\s+/g, ' ')  // Normalize multiple spaces
      .trim();
  };

  // Hydrate form when opening in edit mode or when initialData changes
  React.useEffect(() => {
    if (open && initialData) {
      // Extract status - ensure it's a string (status name) that matches statusOptions
      type StatusLike = string | { name?: string } | null | undefined;
      let statusValue: StatusLike = initialData.status as StatusLike;
      if (statusValue && typeof statusValue === 'object' && 'name' in statusValue && statusValue.name) {
        statusValue = statusValue.name;
      }
      // Convert status to string
      const originalStatusValue = typeof statusValue === 'string' ? statusValue : '';
      let finalStatusValue = originalStatusValue;
      
      // Try to match the status with available options (case-insensitive, handles underscores/spaces)
      if (originalStatusValue && statusOptions.length > 0) {
        const normalizedStatusValue = normalizeStatusForMatching(originalStatusValue);
        const matchedOption = statusOptions.find(opt => {
          const normalizedOptionValue = normalizeStatusForMatching(opt.value);
          const normalizedOptionLabel = normalizeStatusForMatching(opt.label);
          return normalizedOptionValue === normalizedStatusValue || 
                 normalizedOptionLabel === normalizedStatusValue ||
                 opt.value.toLowerCase() === originalStatusValue.toLowerCase() ||
                 opt.label.toLowerCase() === originalStatusValue.toLowerCase();
        });
        
        if (matchedOption) {
          finalStatusValue = matchedOption.value; // Use the exact value from options
          console.log('Status matched:', originalStatusValue, '->', matchedOption.value);
        } else {
          // If no match found, log warning with details
          console.warn('Status not matched:', {
            originalValue: originalStatusValue,
            normalizedValue: normalizedStatusValue,
            availableOptions: statusOptions.map(o => ({ 
              value: o.value, 
              label: o.label,
              normalized: normalizeStatusForMatching(o.value)
            }))
          });
          // Keep original status value - it might match when statusOptions are fully loaded
          finalStatusValue = originalStatusValue;
        }
      } else if (originalStatusValue && statusOptions.length === 0) {
        // StatusOptions not loaded yet, keep the original status value
        // This will be matched again when statusOptions are loaded
        finalStatusValue = originalStatusValue;
      }
      
      setFormData(prev => ({
        projectName: initialData.projectName ?? prev.projectName,
        client: initialData.client ?? prev.client,
        description: initialData.description ?? prev.description,
        status: finalStatusValue || prev.status,
        priority: initialData.priority ?? prev.priority,
        startDate: initialData.startDate ?? prev.startDate,
        endDate: initialData.endDate ?? prev.endDate,
        projectManager: initialData.projectManager ?? prev.projectManager,
        teamMembers: initialData.teamMembers ?? prev.teamMembers,
        budget: initialData.budget ?? prev.budget,
      }));
      setErrors({});
    }
  }, [open, initialData, statusOptions]);

  // Clear form whenever dialog closes
  React.useEffect(() => {
    if (!open) {
      setFormData({
        projectName: '',
        client: '',
        description: '',
        status: '',
        priority: '',
        startDate: '',
        endDate: '',
        projectManager: '',
        teamMembers: [],
        budget: 0,
      });
      setErrors({});
      setProjectManagerSearch('');
      setTeamMembersSearch('');
    }
  }, [open]);

  const handleInputChange = (field: keyof ProjectFormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleTeamMemberToggle = (memberId: string) => {
    setFormData(prev => ({
      ...prev,
      teamMembers: prev.teamMembers.includes(memberId)
        ? prev.teamMembers.filter(id => id !== memberId)
        : [...prev.teamMembers, memberId]
    }));
  };

  const removeTeamMember = (memberId: string) => {
    setFormData(prev => ({
      ...prev,
      teamMembers: prev.teamMembers.filter(id => id !== memberId)
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<ProjectFormData> = {};

    if (!formData.projectName.trim()) {
      newErrors.projectName = 'Project name is required';
    }
    if (!formData.client.trim()) {
      newErrors.client = 'Client is required';
    }
    if (!formData.status) {
      newErrors.status = 'Status is required';
    }
    if (!formData.priority) {
      newErrors.priority = 'Priority is required';
    }
    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }
    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }

    // Validate date range
    if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
      newErrors.endDate = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      if (mode === 'create') {
        // Get status ID from the status name
        const statusId = statusIdMap.get(formData.status) || formData.status;
        
        // Create project via API
        const projectData: CreateProjectRequest = {
          projectName: formData.projectName,
          client: formData.client,
          projectDescription: formData.description,
          priority: formData.priority as 'LOW' | 'MEDIUM' | 'HIGH',
          status: statusId as any, // API expects MongoDB ID, not status name
          startDate: formData.startDate,
          endDate: formData.endDate,
          projectManager: formData.projectManager,
          teamMembers: formData.teamMembers,
          budget: formData.budget,
          progressPercentage: 0
        };

        console.log('Sending project data with real employee IDs:', projectData);
        console.log('Project Manager ID:', projectData.projectManager);
        console.log('Team Members IDs:', projectData.teamMembers);

        const createdProject = await projectService.createProject(projectData);
        
        toast.success("Project Created Successfully", {
          description: `"${projectData.projectName}" has been created and added to your project list.`,
        });

        // Call the callback if provided
        if (onProjectCreated) {
          onProjectCreated(createdProject);
        }

        // Reset form
        setFormData({
          projectName: '',
          client: '',
          description: '',
          status: '',
          priority: '',
          startDate: '',
          endDate: '',
          projectManager: '',
          teamMembers: [],
        });
      } else {
        // Edit mode - update project via API
        if (selectedProject) {
          // Get status ID from the status name
          const statusId = statusIdMap.get(formData.status) || formData.status;
          
          const updateData: UpdateProjectRequest = {
            projectName: formData.projectName,
            client: formData.client,
            projectDescription: formData.description,
            priority: formData.priority as 'LOW' | 'MEDIUM' | 'HIGH',
            status: statusId as any, // API expects MongoDB ID, not status name
            startDate: formData.startDate,
            endDate: formData.endDate,
            projectManager: formData.projectManager,
            teamMembers: formData.teamMembers,
            budget: formData.budget,
            progressPercentage: 0
          };

          console.log('Updating project with data:', updateData);
          console.log('Project ID:', selectedProject.id);

          const updatedProject = await projectService.updateProject(selectedProject.id, updateData);
          
          toast.success("Project Updated", {
            description: "Project has been updated successfully.",
          });

          // Call the callback if provided
          if (onProjectCreated) {
            onProjectCreated(updatedProject);
          }
        } else {
          // Fallback to original onSubmit
          if (onSubmit) {
            onSubmit(formData);
          }
        }
      }
      
      setErrors({});
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error creating/updating project:', error);
      
      let errorMessage = "An unexpected error occurred. Please try again.";
      
      if (error.response?.status === 404) {
        errorMessage = mode === 'edit' 
          ? "Update endpoint not found. Please check if the update API is implemented."
          : "Create endpoint not found. Please check if the create API is implemented.";
      } else if (error.response?.status === 405) {
        errorMessage = mode === 'edit'
          ? "Update method not allowed. The API may not support project updates yet."
          : "Create method not allowed. The API may not support project creation yet.";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      toast.error("Error", {
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (mode === 'create') {
      setFormData({
        projectName: '',
        client: '',
        description: '',
        status: '',
        priority: '',
        startDate: '',
        endDate: '',
        projectManager: '',
        teamMembers: [],
        budget: 0,
      });
    }
    setErrors({});
    onOpenChange(false);
  };

  const selectedProjectManager = employees.find(pm => pm.id === formData.projectManager);
  const selectedTeamMembers = employees.filter(member => formData.teamMembers.includes(member.id));

  // Filter functions for search
  const filteredProjectManagers = employees.filter(manager =>
    manager.name.toLowerCase().includes(projectManagerSearch.toLowerCase()) ||
    (manager.email && manager.email.toLowerCase().includes(projectManagerSearch.toLowerCase()))
  );

  const filteredTeamMembers = employees.filter(member =>
    member.name.toLowerCase().includes(teamMembersSearch.toLowerCase()) ||
    (member.employeeId && member.employeeId.toLowerCase().includes(teamMembersSearch.toLowerCase()))
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[rgb(209,209,209)] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-button]:[display:none] [scrollbar-width:thin] [scrollbar-color:rgb(209,209,209)_transparent]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">{mode === 'edit' ? 'Edit Project' : 'Create New Project'}</DialogTitle>
          <DialogDescription>
            {mode === 'edit' ? 'Update the project details below and save your changes.' : 'Fill in the details below to create a new project. All fields marked with * are required.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Project Name */}
            <div className="space-y-2">
              <Label htmlFor="projectName" className="text-sm font-medium">
                Project Name *
              </Label>
              <Input
                id="projectName"
                placeholder="Enter project name"
                value={formData.projectName}
                onChange={(e) => handleInputChange('projectName', e.target.value)}
                className={cn(errors.projectName && "border-red-500")}
              />
              {errors.projectName && (
                <p className="text-sm text-red-500">{errors.projectName}</p>
              )}
            </div>

            {/* Client */}
            <div className="space-y-2">
              <Label htmlFor="client" className="text-sm font-medium">
                Client *
              </Label>
              <Input
                id="client"
                placeholder="Enter client name"
                value={formData.client}
                onChange={(e) => handleInputChange('client', e.target.value)}
                className={cn(errors.client && "border-red-500")}
              />
              {errors.client && (
                <p className="text-sm text-red-500">{errors.client}</p>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-medium">
              Description
            </Label>
            <Textarea
              id="description"
              placeholder="Enter project description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className="min-h-[100px]"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Status */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Status *</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => handleInputChange('status', value)}
              >
                <SelectTrigger className={cn(errors.status && "border-red-500")}>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.status && (
                <p className="text-sm text-red-500">{errors.status}</p>
              )}
            </div>

            {/* Priority */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Priority *</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) => handleInputChange('priority', value)}
              >
                <SelectTrigger className={cn(errors.priority && "border-red-500")}>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.priority && (
                <p className="text-sm text-red-500">{errors.priority}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Start Date */}
            <div className="space-y-2">
              <Label htmlFor="startDate" className="text-sm font-medium">
                Start Date *
              </Label>
              <div className="relative">
                <Input
                  id="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleInputChange('startDate', e.target.value)}
                  className={cn(errors.startDate && "border-red-500")}
                />
                {/* <Calendar className="absolute right-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" /> */}
              </div>
              {errors.startDate && (
                <p className="text-sm text-red-500">{errors.startDate}</p>
              )}
            </div>

            {/* End Date */}
            <div className="space-y-2">
              <Label htmlFor="endDate" className="text-sm font-medium">
                End Date *
              </Label>
              <div className="relative">
                <Input
                  id="endDate"
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleInputChange('endDate', e.target.value)}
                  className={cn(errors.endDate && "border-red-500")}
                />
                {/* <Calendar className="absolute right-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" /> */}
              </div>
              {errors.endDate && (
                <p className="text-sm text-red-500">{errors.endDate}</p>
              )}
            </div>
          </div>

          {/* Project Manager */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Project Manager</Label>
            <Popover open={projectManagerOpen} onOpenChange={setProjectManagerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={projectManagerOpen}
                  className="w-full justify-between"
                  disabled={loadingEmployees}
                >
                  {loadingEmployees ? "Loading..." : selectedProjectManager ? selectedProjectManager.name : "Select project manager..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                <div className="p-2">
                  <Input
                    placeholder="Search project managers..."
                    value={projectManagerSearch}
                    onChange={(e) => setProjectManagerSearch(e.target.value)}
                    className="h-9"
                  />
                </div>
                <div className="max-h-60 overflow-y-auto">
                  {loadingEmployees ? (
                    <div className="p-2 text-sm text-muted-foreground text-center">
                      Loading employees...
                    </div>
                  ) : filteredProjectManagers.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground text-center">
                      No project manager found.
                    </div>
                  ) : (
                    filteredProjectManagers.map((manager) => (
                      <div
                        key={manager.id}
                        onClick={() => {
                          handleInputChange('projectManager', manager.id);
                          setProjectManagerOpen(false);
                          setProjectManagerSearch('');
                        }}
                        className="flex items-center px-2 py-2 hover:bg-accent cursor-pointer"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            formData.projectManager === manager.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                          <User className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">{manager.name}</div>
                          {manager.email && (
                            <div className="text-sm text-muted-foreground">{manager.email}</div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Team Members */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Team Members</Label>
            <Popover open={teamMembersOpen} onOpenChange={setTeamMembersOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={teamMembersOpen}
                  className="w-full justify-between"
                  disabled={loadingEmployees}
                >
                  {loadingEmployees ? "Loading..." : selectedTeamMembers.length > 0 
                    ? `${selectedTeamMembers.length} member${selectedTeamMembers.length > 1 ? 's' : ''} selected`
                    : "Select team members..."
                  }
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                <div className="p-2">
                  <Input
                    placeholder="Search team members..."
                    value={teamMembersSearch}
                    onChange={(e) => setTeamMembersSearch(e.target.value)}
                    className="h-9"
                  />
                </div>
                <div className="max-h-60 overflow-y-auto">
                  {loadingEmployees ? (
                    <div className="p-2 text-sm text-muted-foreground text-center">
                      Loading employees...
                    </div>
                  ) : filteredTeamMembers.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground text-center">
                      No team member found.
                    </div>
                  ) : (
                    filteredTeamMembers.map((member) => (
                      <div
                        key={member.id}
                        onClick={() => handleTeamMemberToggle(member.id)}
                        className="flex items-center px-2 py-2 hover:bg-accent cursor-pointer"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            formData.teamMembers.includes(member.id) ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                          <User className="h-4 w-4 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">{member.name}</div>
                          {member.employeeId && (
                            <div className="text-sm text-muted-foreground">ID: {member.employeeId}</div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </PopoverContent>
            </Popover>
            
            {/* Selected Team Members Chips */}
            {selectedTeamMembers.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedTeamMembers.map((member) => (
                  <Badge key={member.id} variant="secondary" className="flex items-center gap-2 px-2 py-1">
                    <div className="w-4 h-4 bg-green-100 rounded-full flex items-center justify-center">
                      <User className="h-2.5 w-2.5 text-green-600" />
                    </div>
                    {member.name}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-red-500"
                      onClick={() => removeTeamMember(member.id)}
                    />
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Budget */}
          <div className="space-y-2">
            <Label htmlFor="budget" className="text-sm font-medium">
              Budget
            </Label>
            <Input
              id="budget"
              type="number"
              placeholder="Enter project budget"
              value={formData.budget || ''}
              onChange={(e) => handleInputChange('budget', Number(e.target.value) || 0)}
              min="0"
              step="1000"
            />
          </div>

          <DialogFooter className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : mode === 'edit' ? 'Update Project' : 'Create Project'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}